import React, { useState } from "react";
import { Box, Typography, Card, CardContent } from "@mui/material";
import { keyframes, styled } from "@mui/material/styles";
import { Park, WaterDrop, Grass } from "@mui/icons-material";

// Nature-Inspired Animation Keyframes
const gentleRise = keyframes`
  0% {
    opacity: 0;
    transform: translateY(40px) scale(0.96);
  }
  100% {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
`;

const leafFloat = keyframes`
  0%, 100% { 
    transform: translateY(0px) rotate(0deg);
  }
  33% { 
    transform: translateY(-8px) rotate(2deg);
  }
  66% { 
    transform: translateY(-4px) rotate(-1deg);
  }
`;

const sunGlow = keyframes`
  0%, 100% { 
    box-shadow: 
      0 0 25px rgba(76, 175, 80, 0.3),
      0 0 50px rgba(139, 195, 74, 0.2),
      inset 0 0 15px rgba(76, 175, 80, 0.1);
  }
  50% { 
    box-shadow: 
      0 0 35px rgba(76, 175, 80, 0.5),
      0 0 70px rgba(139, 195, 74, 0.3),
      inset 0 0 25px rgba(76, 175, 80, 0.2);
  }
`;

const waterRipple = keyframes`
  0% {
    background-position: 0% 0%;
    opacity: 0.8;
  }
  50% {
    background-position: 100% 100%;
    opacity: 1;
  }
  100% {
    background-position: 0% 0%;
    opacity: 0.8;
  }
`;

const breezeSway = keyframes`
  0%, 100% { 
    transform: translateX(0px) rotate(0deg);
  }
  25% { 
    transform: translateX(2px) rotate(0.5deg);
  }
  75% { 
    transform: translateX(-1px) rotate(-0.3deg);
  }
`;

const growthBloom = keyframes`
  0% {
    transform: scale(0.8);
    opacity: 0;
  }
  70% {
    transform: scale(1.05);
    opacity: 0.8;
  }
  100% {
    transform: scale(1);
    opacity: 1;
  }
`;

const cloudFloat = keyframes`
  0% {
    transform: translateX(-100px) translateY(0px);
  }
  50% {
    transform: translateX(50px) translateY(-10px);
  }
  100% {
    transform: translateX(100px) translateY(0px);
  }
`;

// Nature-Inspired Styled Components
const NatureContainer = styled(Box)(({ theme }) => ({
  width: "100%",
  minHeight: "50vh",
  borderRadius: "40px",
  background:
    "linear-gradient(160deg, #2f662fff 0%, #a5d6a7 50%, #81c784 100%)",
  //   backgroundSize: "400% 400%",
  animation: `${waterRipple} 20s ease infinite`,
  position: "relative",
  overflow: "hidden",
  [theme.breakpoints.up("md")]: {
    padding: theme.spacing(1),
  },
  "&::before": {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: `radial-gradient(circle at 20% 80%, rgba(255,255,255,0.3) 0%, transparent 50%),
       radial-gradient(circle at 80% 20%, rgba(255,255,255,0.2) 0%, transparent 50%),
       radial-gradient(circle at 40% 40%, rgba(255,255,255,0.1) 0%, transparent 50%)`,
    pointerEvents: "none",
  },
}));

const HeaderNature = styled(Box)(({ theme }) => ({
  textAlign: "center",
  marginBottom: theme.spacing(1),
  background: "rgba(255, 255, 255, 0.9)",
  backdropFilter: "blur(10px)",
  border: "2px solid rgba(76, 175, 80, 0.2)",
  borderRadius: "24px",
  position: "relative",
  overflow: "hidden",
  animation: `${gentleRise} 1s ease-out`,
  boxShadow: "0 8px 32px rgba(76, 175, 80, 0.1)",
  [theme.breakpoints.down("sm")]: {
    padding: theme.spacing(2),
    marginBottom: theme.spacing(3),
  },
  "&::before": {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    height: "4px",
    background: "linear-gradient(90deg, #4caf50, #81c784, #4caf50)",
    animation: `${waterRipple} 3s ease-in-out infinite`,
  },
}));

const NatureTitle = styled(Typography)(({ theme }) => ({
  fontSize: "3rem",
  fontWeight: 800,
  background:
    "linear-gradient(135deg, #174519ff 0%, #4caf50 50%, #519a54ff 100%)",
  backgroundClip: "text",
  WebkitBackgroundClip: "text",
  WebkitTextFillColor: "transparent",
  marginBottom: theme.spacing(1),
  fontFamily: "'Playfair Display', serif",
  textShadow: "2px 2px 4px rgba(0,0,0,0.1)",
  [theme.breakpoints.down("md")]: {
    fontSize: "2.5rem",
  },
  [theme.breakpoints.down("sm")]: {
    fontSize: "2rem",
  },
}));

const NatureSubtitle = styled(Typography)(({ theme }) => ({
  fontSize: "1.4rem",
  fontWeight: 400,
  color: "#388e3c",
  fontFamily: "'Inter', sans-serif",
  letterSpacing: "0.5px",
  [theme.breakpoints.down("sm")]: {
    fontSize: "1.1rem",
  },
}));

const NatureGrid = styled(Box)(({ theme }) => ({
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
  gap: theme.spacing(1),
  [theme.breakpoints.down("sm")]: {
    gridTemplateColumns: "1fr",
    gap: theme.spacing(2),
    padding: theme.spacing(1),
  },
}));

const NatureCard = styled(Card, {
  shouldForwardProp: (prop) => prop !== "index" && prop !== "hovered",
})(({ index, hovered, theme }) => ({
  background: hovered
    ? "linear-gradient(145deg, rgba(255,255,255,0.95) 0%, rgba(248,252,248,0.98) 100%)"
    : "linear-gradient(145deg, rgba(255,255,255,0.9) 0%, rgba(248,252,248,0.95) 100%)",
  backdropFilter: "blur(10px)",
  border: hovered
    ? "2px solid rgba(76, 175, 80, 0.6)"
    : "2px solid rgba(76, 175, 80, 0.2)",
  borderRadius: "20px",
  padding: "0",
  position: "relative",
  overflow: "hidden",
  animation: `${gentleRise} 0.8s ease-out ${index * 0.15}s both`,
  transition: "all 0.4s cubic-bezier(0.25, 0.46, 0.45, 0.94)",
  boxShadow: hovered
    ? "0 20px 40px rgba(76, 175, 80, 0.15), 0 8px 25px rgba(0, 0, 0, 0.05)"
    : "0 8px 25px rgba(0, 0, 0, 0.08), 0 4px 12px rgba(0, 0, 0, 0.03)",

  "&::before": {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    height: "4px",
    background: "linear-gradient(90deg, #4caf50, #81c784, #a5d6a7)",
    transform: hovered ? "scaleX(1)" : "scaleX(0)",
    transformOrigin: "left",
    transition: "transform 0.4s ease",
  },

  "&:hover": {
    transform: "translateY(-8px) scale(1.02)",
    animation: `${sunGlow} 3s ease-in-out infinite, ${breezeSway} 6s ease-in-out infinite`,
  },

  [theme.breakpoints.down("sm")]: {
    borderRadius: "16px",
    "&:hover": {
      transform: "translateY(-4px) scale(1.01)",
    },
  },
}));

const NatureStats = styled(Box, {
  shouldForwardProp: (prop) => prop !== "hovered",
})(({ hovered, theme }) => ({
  display: "inline-flex",
  alignItems: "center",
  background: "linear-gradient(135deg, #4caf50 0%, #388e3c 100%)",
  color: "#fff",
  padding: theme.spacing(1, 2),
  borderRadius: "20px",
  fontWeight: 700,
  fontSize: "1rem",
  transition: "all 0.3s ease",
  transform: hovered ? "scale(1.05) translateY(-2px)" : "scale(1)",
  boxShadow: "0 4px 15px rgba(76, 175, 80, 0.3)",
  fontFamily: "'Inter', sans-serif",
  [theme.breakpoints.down("sm")]: {
    fontSize: "0.9rem",
    padding: theme.spacing(0.75, 1.5),
  },
}));

const FloatingLeaf = styled(Box)(({ index, theme }) => ({
  position: "absolute",
  width: "12px",
  height: "12px",
  background: "linear-gradient(45deg, #4caf50, #81c784)",
  borderRadius: "50% 0 50% 50%",
  animation: `${leafFloat} ${4 + index}s ease-in-out infinite`,
  animationDelay: `${index * 0.5}s`,
  opacity: 0.6,
  [theme.breakpoints.down("sm")]: {
    width: "8px",
    height: "8px",
  },
}));

const NatureDetail = styled(Box, {
  shouldForwardProp: (prop) => prop !== "hovered" && prop !== "delay",
})(({ hovered, delay, theme }) => ({
  display: "flex",
  alignItems: "flex-start",
  marginBottom: theme.spacing(1.5),
  padding: theme.spacing(0.5, 0),
  animation: hovered ? `${growthBloom} 0.5s ease-out ${delay}ms both` : "none",
  "&::before": {
    content: '"🌱"',
    marginRight: theme.spacing(1.5),
    fontSize: "1.1rem",
    marginTop: "2px",
  },
  [theme.breakpoints.down("sm")]: {
    marginBottom: theme.spacing(1),
    "&::before": {
      marginRight: theme.spacing(1),
      fontSize: "1rem",
    },
  },
}));

const NatureProgress = styled(Box, {
  shouldForwardProp: (prop) => prop !== "progress" && prop !== "hovered",
})(({ progress, hovered, theme }) => ({
  width: "100%",
  height: "6px",
  background: "rgba(76, 175, 80, 0.2)",
  borderRadius: "3px",
  overflow: "hidden",
  margin: theme.spacing(1, 0),
  position: "relative",
  "&::after": {
    content: '""',
    display: "block",
    height: "100%",
    background: "linear-gradient(90deg, #4caf50, #81c784)",
    borderRadius: "3px",
    width: hovered ? `${progress}%` : "0%",
    transition: "width 1s cubic-bezier(0.34, 1.56, 0.64, 1)",
    transitionDelay: "0.3s",
  },
  "&::before": {
    content: '""',
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background:
      "linear-gradient(90deg, transparent, rgba(255,255,255,0.4), transparent)",
    animation: hovered ? `${waterRipple} 2s linear infinite` : "none",
  },
}));

const CloudElement = styled(Box)(({ theme }) => ({
  position: "absolute",
  background: "rgba(255, 255, 255, 0.8)",
  borderRadius: "50%",
  animation: `${cloudFloat} 20s linear infinite`,
  [theme.breakpoints.down("sm")]: {
    display: "none", // Hide clouds on mobile for better performance
  },
}));

const CommunityMobilization = () => {
  const [hoveredIndex, setHoveredIndex] = useState(null);

  const features = [
    {
      id: 1,
      title: "MODEL VILLAGE",
      subtitle: "MISALI GAON INITIATIVE",
      description:
        "Transforming Rural Punjab through sustainable, inclusive, and climate-resilient municipal infrastructure",
      icon: Park,
      stats: "PKR 50 BILLION",
      color: "#2e7d32",
      details: [
        { label: "Coverage", value: "414 villages across 10 divisions" },
        { label: "Population", value: "2.6M People | 400K Households" },
        { label: "Water Systems", value: "Direct connections & RO plants" },
        { label: "Drainage", value: "Modern wastewater treatment" },
        { label: "Infrastructure", value: "Solar lighting & public parks" },
        { label: "SDG Alignment", value: "Goals 6, 11, 3, 13" },
      ],
      leaves: 3,
    },
    {
      id: 2,
      title: "SANITATION SOLUTIONS",
      subtitle: "NATURE-BASED TREATMENT",
      description:
        "Comprehensive wastewater management with innovative nature-based solutions",
      icon: WaterDrop,
      stats: "PKR 18.32B INVESTMENT",
      color: "#1976d2",
      details: [
        { label: "Cities", value: "11 urban centers" },
        { label: "Investment", value: "PKR 18,320 Million" },
        { label: "Beneficiaries", value: "3.79M people" },
        { label: "Pollution Reduction", value: "BOD 70-90% | COD 60-85%" },
        { label: "Progress", value: "75% Complete", progress: 75 },
        { label: "Urban Forest", value: "172,251 native trees" },
        { label: "Impact", value: "Significant GHG reduction" },
      ],
      leaves: 4,
    },
    {
      id: 3,
      title: "FLOATING WETLANDS",
      subtitle: "INNOVATIVE BIO-TECH",
      description:
        "Revolutionary natural wastewater treatment using floating wetlands technology",
      icon: Grass,
      stats: "90% IMPLEMENTED",
      color: "#388e3c",
      details: [
        { label: "Technology", value: "Floating wetland ecosystems" },
        { label: "Partner", value: "UNICEF funded" },
        { label: "Scope", value: "4 major cities" },
        { label: "Beneficiaries", value: "432,525 residents" },
        { label: "Efficiency", value: "BOD/COD 35-40% reduction" },
        { label: "Progress", value: "90% Deployed", progress: 90 },
        { label: "Features", value: "Chemical-free natural treatment" },
      ],
      leaves: 5,
    },
  ];

  return (
    <NatureContainer>
      {/* Animated cloud elements */}
      <CloudElement
        sx={{ top: "10%", left: "5%", width: "120px", height: "60px" }}
      />
      <CloudElement
        sx={{
          top: "20%",
          right: "8%",
          width: "150px",
          height: "70px",
          animationDelay: "5s",
        }}
      />
      <CloudElement
        sx={{
          bottom: "25%",
          left: "10%",
          width: "100px",
          height: "50px",
          animationDelay: "10s",
        }}
      />

      {/* Floating leaf background elements */}
      <Box
        sx={{
          position: "absolute",
          top: "15%",
          right: "5%",
          width: "100px",
          height: "100px",
          background:
            "radial-gradient(circle, rgba(76, 175, 80, 0.1) 0%, transparent 70%)",
          animation: `${leafFloat} 8s ease-in-out infinite`,
        }}
      />
      <Box
        sx={{
          position: "absolute",
          bottom: "10%",
          left: "5%",
          width: "80px",
          height: "80px",
          background:
            "radial-gradient(circle, rgba(76, 175, 80, 0.08) 0%, transparent 70%)",
          animation: `${leafFloat} 10s ease-in-out infinite reverse`,
        }}
      />

      <HeaderNature>
        <NatureTitle>Sustainable Punjab</NatureTitle>
        <NatureSubtitle>
          Nature-Inspired Infrastructure Development
        </NatureSubtitle>
      </HeaderNature>

      <NatureGrid>
        {features.map((feature, index) => {
          const Icon = feature.icon;
          const isHovered = hoveredIndex === index;

          return (
            <NatureCard
              key={feature.id}
              index={index}
              hovered={isHovered}
              onMouseEnter={() => setHoveredIndex(index)}
              onMouseLeave={() => setHoveredIndex(null)}
            >
              {/* Floating leaves */}
              {Array.from({ length: feature.leaves }).map((_, leafIndex) => (
                <FloatingLeaf
                  key={leafIndex}
                  index={leafIndex}
                  sx={{
                    top: `${10 + leafIndex * 20}%`,
                    right: `${5 + leafIndex * 5}%`,
                  }}
                />
              ))}

              <CardContent
                sx={{
                  display: "flex",
                  alignItems: "flex-start",
                  gap: 1,
                  padding: 2,
                  position: "relative",
                  zIndex: 2,
                }}
              >
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  <Typography
                    variant="h4"
                    sx={{
                      fontWeight: 700,
                      //   mb: 1,
                      fontSize: { xs: "1rem", sm: "1.2rem", md: "1.4rem" },
                      lineHeight: 1.2,
                      color: feature.color,
                      fontFamily: "'Playfair Display', serif",
                    }}
                  >
                    <Icon fontSize="inherit" /> {feature.title}
                  </Typography>

                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 700,
                      mb: `1`,
                      fontSize: { xs: "1rem", sm: "1.2rem" },
                      color: "#666",
                      fontFamily: "'Inter', sans-serif",
                      fontStyle: "italic",
                    }}
                  >
                    {feature.subtitle}
                  </Typography>

                  <Typography
                    variant="body1"
                    sx={{
                      lineHeight: 1.5,
                      fontSize: { xs: "0.9rem", sm: "1rem" },
                      mb: 3,
                      color: "#555",
                      fontFamily: "'Inter', sans-serif",
                      borderLeft: `3px solid ${feature.color}33`,
                      paddingLeft: 2,
                      background: "rgba(76, 175, 80, 0.03)",
                      padding: "6px 8px",
                      borderRadius: "0 12px 12px 0",
                    }}
                  >
                    {feature.description}
                  </Typography>

                  <Box sx={{ mb: 3 }}>
                    {feature.details.map((detail, detailIndex) => (
                      <NatureDetail
                        key={detailIndex}
                        hovered={isHovered}
                        delay={detailIndex * 80}
                      >
                        <Typography
                          variant="body2"
                          sx={{
                            fontSize: { xs: "1rem", sm: "1.1rem" },
                            lineHeight: 1.4,
                            color: "#444",
                            fontFamily: "'Inter', sans-serif",
                            "& strong": {
                              color: feature.color,
                              fontWeight: 600,
                            },
                          }}
                        >
                          <strong>{detail.label}:</strong> {detail.value}
                        </Typography>
                        {detail.progress && (
                          <NatureProgress
                            progress={detail.progress}
                            hovered={isHovered}
                          />
                        )}
                      </NatureDetail>
                    ))}
                  </Box>

                  <Box sx={{ display: "flex", justifyContent: "flex-start" }}>
                    <NatureStats hovered={isHovered}>
                      {feature.stats}
                    </NatureStats>
                  </Box>
                </Box>
              </CardContent>
            </NatureCard>
          );
        })}
      </NatureGrid>
    </NatureContainer>
  );
};

export default CommunityMobilization;
